import {
	AppBar,
	Slide,
	Toolbar,
	Box,
	IconButton,
	Typography,
	Menu,
	Container,
	Avatar,
	Button,
	Tooltip,
	useScrollTrigger,
	MenuItem,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	SwipeableDrawer,
	List,
	styled,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CarCrashIcon from '@mui/icons-material/CarCrash';
import HelpIcon from '@mui/icons-material/Help';
import MapIcon from '@mui/icons-material/Map';
import PaymentsIcon from '@mui/icons-material/Payments';
import PersonIcon from '@mui/icons-material/Person';
import React, { FC } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { useTelematicsFeature } from '@acorn-portal/shared/src/hooks/useTelematicsFeature';
import { useAuth } from 'react-oidc-context';
import { getNavigationFromState } from './navbar-utils';

interface Props {
	/**
	 * Injected by the documentation to work in an iframe.
	 * You won't need it on your project.
	 */

	window?: () => Window;
	children: React.ReactElement;
}

const HideOnScroll = (props: Props) => {
	const { children, window } = props;
	// Note that you normally won't need to set the window ref as useScrollTrigger
	// will default to window.
	// This is only being set here because the demo is in an iframe.
	const trigger = useScrollTrigger({
		target: window ? window() : undefined,
	});

	return (
		<Slide appear={false} direction='down' in={!trigger}>
			{children}
		</Slide>
	);
};

interface INavBarProps {
	Logo: React.FC<React.SVGProps<SVGSVGElement>>;
	logoHref: string;
	MobileLogo?: React.FC<React.SVGProps<SVGSVGElement>>;
}

interface IPage {
	title: string;
	type: 'navigation' | 'settings';
	path?: string;
	action?: () => void;
	icon?: React.ReactNode;
	hideOnDesktop?: boolean;
	hide?: boolean;
}

const ImageContainer = styled('div')({
	margin: '0 auto',
	'> a': {
		display: 'flex',
		alignItems: 'center',
		marginRight: '1em',
	},
});

const Navbar: FC<INavBarProps> = ({ Logo, logoHref, MobileLogo }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const auth = useAuth();

	const userName = auth.user?.profile?.name;

	// Telematics feature flag - controls whether telematics nav items are shown
	const isTelematicsFeatureEnabled = useTelematicsFeature();

	const policyDetails = useAppSelector((redux) => redux.fullPolicy);
	const { policyExists } = useAppSelector((redux) => redux.telematicsEnabledPolicy);
	const isTelematicsPolicyEnabled = policyExists?.exists ?? false;

	// Combined check: feature flag must be on AND policy must be telematics-enabled
	const isTelematicsEnabled = isTelematicsFeatureEnabled && isTelematicsPolicyEnabled;

	const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(null);
	const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);

	const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorElNav(event.currentTarget);
	};
	const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorElUser(event.currentTarget);
	};

	const handleCloseNavMenu = () => {
		setAnchorElNav(null);
	};

	const handleCloseUserMenu = (path: string) => {
		setAnchorElUser(null);
		if (path) navigate(path);
	};

	const handlePageClick = (page: IPage) => {
		if (page.path && page.type === 'navigation') {
			handleCloseNavMenu();
			if (location.pathname !== page.path) {
				const fromState = getNavigationFromState(page.path, location.pathname, location.state?.from);
				navigate(page.path, { state: { from: fromState } });
			}
			return;
		}

		if (page.path && page.type === 'settings') {
			handleCloseUserMenu(page.path);
			return;
		}

		if (page.action) {
			page.action();
		}
	};

	const stringAvatar = (name: string) => {
		return {
			sx: {
				bgcolor: '#B1B1B1',
			},
			children: `${name.split(' ')[0][0]}${name.split(' ')[1][0]}`.toUpperCase(),
		};
	};

	const handleLogOut = () => {
		sessionStorage.clear();
		localStorage.clear();
		auth.removeUser();
		auth.signoutSilent({
			id_token_hint: auth.user?.id_token,
		});
	};

	const pages: IPage[] = [
		{
			title: 'Policy Details',
			path: ROUTE_PATHS.policyDetails,
			type: 'navigation',
			icon: <CarCrashIcon />,
		},
		{
			title: 'Renewals',
			path: ROUTE_PATHS.policyRenewals,
			type: 'navigation',
			icon: <AutorenewIcon />,
		},
		{
			title: 'Payments',
			path: `${ROUTE_PATHS.paymentPlan}/${policyDetails.policyToken}`,
			type: 'navigation',
			icon: <PaymentsIcon />,
			hide:
				window.location.href.includes('/home') ||
				window.location.href.includes('/help') ||
				window.location.href.includes('/policy-renewals') ||
				!policyDetails.isDirectDebitPaymentMethod,
		},
		{
			title: 'Trips',
			path: '/portal/trips',
			type: 'navigation',
			icon: <MapIcon />,
			hide:
				window.location.href.includes('/home') ||
				window.location.href.includes('/help') ||
				window.location.href.includes('/policy-renewals') ||
				!isTelematicsEnabled,
		},
		{
			title: 'Help',
			path: ROUTE_PATHS.help,
			type: 'navigation',
			icon: <HelpIcon />,
		},
		{
			title: 'Change Password',
			path: ROUTE_PATHS.changePassword,
			type: 'settings',
		},
		{ title: 'Log Out', action: handleLogOut, type: 'settings' },
	];
	return (
		<HideOnScroll>
			<AppBar position='static'>
				<Container maxWidth='xl'>
					<Toolbar disableGutters>
						<Box sx={{ display: { xs: 'flex', md: 'none' } }}>
							<IconButton
								size='large'
								aria-label='account of current user'
								aria-controls='menu-appbar'
								aria-haspopup='true'
								onClick={handleOpenNavMenu}
								sx={(theme) => ({
									color: theme.palette.common.white,
								})}
							>
								<MenuIcon />
							</IconButton>
							<SwipeableDrawer
								id='menu-appbar'
								anchor='left'
								onOpen={handleOpenNavMenu}
								open={Boolean(anchorElNav)}
								onClose={handleCloseNavMenu}
								sx={(theme) => ({
									'.MuiDrawer-paper': {
										backgroundColor: theme.palette.secondary.dark,
										color: theme.palette.common.white,
									},
									svg: {
										color: theme.palette.common.white,
									},
								})}
							>
								<Box marginTop={2} />
								<ImageContainer>
									<a href={logoHref} aria-label='Go to homepage' target='_blank' rel='noreferrer' id='link'>
										{MobileLogo ? (
											<MobileLogo aria-label='brand logo' role='img' />
										) : (
											<Logo aria-label='brand logo' role='img' />
										)}
									</a>
								</ImageContainer>
								<Box marginTop={1} />

								<List sx={{ mx: 2 }}>
									{pages
										.filter((page) => page.type === 'navigation' && !page.hide)
										.map((page) => (
											<ListItem
												key={page.title}
												divider
												sx={(theme) => ({
													borderBottomColor: theme.palette.common.white,
												})}
											>
												<ListItemButton onClick={() => handlePageClick(page)}>
													{page.icon ? <ListItemIcon>{page.icon}</ListItemIcon> : null}
													<ListItemText primary={page.title} />
												</ListItemButton>
											</ListItem>
										))}
								</List>
							</SwipeableDrawer>
						</Box>
						<ImageContainer>
							<a href={logoHref} aria-label='Go to homepage' target='_blank' rel='noreferrer' id='link'>
								<Logo aria-label='brand logo' role='img' />
							</a>
						</ImageContainer>
						<Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
							{pages
								.filter((page) => page.type === 'navigation' && !page.hideOnDesktop && !page.hide)
								.map((page) => (
									<Button
										key={page.title}
										onClick={() => handlePageClick(page)}
										sx={(theme) => ({
											my: 2,
											color: theme.palette.navigationButtonColor.main,
											display: 'block',
										})}
									>
										{page.title}
									</Button>
								))}
						</Box>

						<Box sx={{ flexGrow: 0 }}>
							<Tooltip title='Open settings'>
								<IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
									{userName ? (
										<Avatar {...stringAvatar(userName)} />
									) : (
										<Avatar>
											<PersonIcon />
										</Avatar>
									)}
								</IconButton>
							</Tooltip>
							<Menu
								sx={{ mt: '45px' }}
								id='menu-appbar'
								anchorEl={anchorElUser}
								anchorOrigin={{
									vertical: 'top',
									horizontal: 'right',
								}}
								keepMounted
								transformOrigin={{
									vertical: 'top',
									horizontal: 'right',
								}}
								open={Boolean(anchorElUser)}
								onClose={() => handleCloseUserMenu('')}
							>
								{pages
									.filter((page) => page.type === 'settings')
									.map((setting) => (
										<MenuItem key={setting.title} onClick={() => handlePageClick(setting)}>
											<Typography textAlign='center'>{setting.title}</Typography>
										</MenuItem>
									))}
							</Menu>
						</Box>
					</Toolbar>
				</Container>
			</AppBar>
		</HideOnScroll>
	);
};
export default Navbar;
